import { ReactNode, useEffect } from 'react';
import {
  CorporateAccountOpenProfileDetailsPages,
  useCorporateAccountOpenProfileDetailsPageTypeStore,
} from 'views/pages/accountOpenProfileDetails/corporateProfileDetails/hooks';
import { CorporateAccountEstablishRequestParam, useCorporateAccountEstablishStore } from '../hooks';
import { AboutRulerType, RelastionShipType, RulerImportantPublicWorkerType } from '../rulerInfo/hooks';
import { CorporateAddressConfirmSelectType } from '../corporateInfo/hooks';
import {
  ChangeAccountInfoMutationVariables,
  OpenAccountMutationVariables,
  useChangeAccountInfoMutation,
  useOpenAccountMutation,
} from 'views/utils/graphql/graphql';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import {
  findCorporateBusinessContentId,
  findInvestorCategoryInfoId,
  findRelastionShipName,
  findSettlementDayId,
  findSettlementMonthId,
  findCorporateLeaseFundPurposeId,
  findDomesticRealEstatePurposeId,
  findCorporateOverseasRealEstatePurposeId,
  ChangeAccountInfoExecuteType,
  FatcaStatus,
} from 'views/utils/enums';
import { DateFormatType, formatDate } from 'views/utils/converter';
import { TemplateProps } from '../common';
import { PageStep } from 'views/pages/accountInfo/hooks';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { InvestorCategory } from 'views/utils/enums';
import { AuthModeSelectType } from '../tfAuthentication/hooks';
import { AccountType } from 'views/templates/module/bankDetailsInput/hooks';
import { useFormik } from 'formik';

type EntryConfirmType = {
  agreeToAccountOpenProfileDetails: boolean;
};

export type DisplayItemProps = {
  label: string;
  value: string | ReactNode;
  hidden?: boolean;
};

export type EntryConfirmProps = {
  isMailChanged?: (mailAddress: string) => boolean | undefined;
} & TemplateProps;

export const useEntryConfirm = ({ isUpdate, setPageStep, isMailChanged }: EntryConfirmProps) => {
  const { param, add } = useCorporateAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = useCorporateAccountOpenProfileDetailsPageTypeStore();
  const [_openAccountResponse, openAccount] = useOpenAccountMutation();
  const [_changeAccountResponse, changeAccount] = useChangeAccountInfoMutation();
  const { showError } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const initialValues: EntryConfirmType = {
    agreeToAccountOpenProfileDetails: false,
  };

  const formik = useFormik<EntryConfirmType>({
    enableReinitialize: isUpdate,
    initialValues,
    async onSubmit() {
      add({
        ...param,
        ...formik.values,
      });
      setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.entryConfirm);
    },
    // validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const agreeToAccountOpenProfileDetailsProps = {
    checked: formik.values.agreeToAccountOpenProfileDetails,
    onChange: () =>
      formik.setFieldValue('agreeToAccountOpenProfileDetails', !formik.values.agreeToAccountOpenProfileDetails),
    error: formik.errors.agreeToAccountOpenProfileDetails,
  };

  const execute = {
    onClick: async () => {
      if (isUpdate) {
        if (isMailChanged && isMailChanged(param.dealerMailAddress)) {
          await updateMailCheck();
        } else {
          await executeUpdateAccount();
        }
      } else {
        await executeOpenAccount();
      }
    },
  };

  const updateMailCheck = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        setPageStep && setPageStep(PageStep.SENDMAIL);
      },
    },
    [showError],
  );

  const executeOpenAccount = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const data: OpenAccountMutationVariables = {
          input: {
            CorporateAccount: {
              UniqueID: param.uniqueId,
              RepresentAndDealerAreSame: param.dealerQuestion,
              HaveEKYCDocument: param.identificationQuestion,
              CanTakePicture: param.selfieQuestion,
              ConfirmResponsibility: param.confirmResponsibility,
              CorporateNumber: param.corporateNumber,
              CorporateName: param.corporateName,
              CorporateFurigana: param.corporateFurigana,
              CorporatePostCode: param.corporatePostCode,
              CorporatePrefecture: param.corporatePrefecture,
              CorporateCityCountry: param.corporateCityCountry,
              CorporateTownStreetAddress: param.corporateTownStreetAddress,
              CorporateBuildingNameAndRoomNumber: param.corporateBuildingNameAndRoomNumber,
              RegisteredCorporatePostCode: param.registeredCorporatePostCode,
              RegisteredCorporatePrefecture: param.registeredCorporatePrefecture,
              RegisteredCorporateCityCountry: param.registeredCorporateCityCountry,
              RegisteredCorporateTownStreetAddress: param.registeredCorporateTownStreetAddress,
              RegisteredCorporateBuildingNameAndRoomNumber: param.registeredCorporateBuildingNameAndRoomNumber,
              CorporateSettlementMonth: [findSettlementMonthId(param.corporateSettlementMonth)!],
              CorporateSettlementDay: findSettlementDayId(param.corporateSettlementDay),
              CorporateEstablishmentDate: formatDate(param.corporateEstablishmentDate, DateFormatType.Slash_yyyyMMdd),
              CorporateMainBusinessContent: findCorporateBusinessContentId(param.corporateMainBusinessContent),
              CorporateMainBusinessContentOtherText: param.corporateMainBusinessContentOtherText,
              CorporateOtherBusinessContent: findCorporateBusinessContentId(param.corporateOtherBusinessContent),
              CorporateOtherBusinessContentOtherText: param.corporateOtherBusinessContentOtherText,
              CorporateCapital: Number(param.corporateCapital),
              CorporateNumberOfEmployees: Number(param.corporateNumberOfEmployees),
              CorporateHomepage: param.corporateHomepage,
              ElectronicDocumentAgreement: param.electronicDocumentAgreement,
              ElectronicDocumentAgreementDateTime: param.electronicDocumentAgreementDateTime,
              LeaseFundPurpose: param.leaseFundPurpose
                .filter((x) => x.checked)
                .map((x) => findCorporateLeaseFundPurposeId(x.label) ?? null),
              LeaseFundPurposeOtherText: param.leaseFundPurposeOtherText,
              DomesticRealEstatePurpose: param.domesticRealEstatePurpose
                .filter((x) => x.checked)
                .map((x) => findDomesticRealEstatePurposeId(x.label) ?? null),
              DomesticRealEstatePurposeOtherText: param.domesticRealEstatePurposeOtherText,
              OverseasRealEstatePurpose: param.overseasRealEstatePurpose
                .filter((x) => x.checked)
                .map((x) => findCorporateOverseasRealEstatePurposeId(x.label) ?? null),
              OverseasRealEstatePurposeOtherText: param.overseasRealEstatePurposeOtherText,
              InvestorCategory: param.investorCategory,
              ContactPhoneNumber: param.contactPhoneNumber,
              ContactFaxNumber: param.contactFaxNumber,
              RepresentFirstName: param.representFirstName,
              RepresentLastName: param.representLastName,
              RepresentFirstNameKana: param.representFirstNameKana,
              RepresentLastNameKana: param.representLastNameKana,
              RepresentBirthDay: formatDate(param.representBirthDay, DateFormatType.Slash_yyyyMMdd),
              RepresentPostCode: param.representPostCode,
              RepresentPrefecture: param.representPrefecture,
              RepresentCityCountry: param.representCityCountry,
              RepresentTownStreetAddress: param.representTownStreetAddress,
              RepresentBuildingNameAndRoomNumber: param.representBuildingNameAndRoomNumber,
              RepresentMobilePhoneNumber: param.representMobilePhoneNumber,
              RepresentMailAddress: param.representMailAddress,
              RepresentPosition: param.representPosition,
              DealerFirstName: param.dealerFirstName,
              DealerLastName: param.dealerLastName,
              DealerFirstNameKana: param.dealerFirstNameKana,
              DealerLastNameKana: param.dealerLastNameKana,
              DealerBirthDay: formatDate(param.dealerBirthDay, DateFormatType.Slash_yyyyMMdd),
              DealerPostCode: param.dealerPostCode,
              DealerPrefecture: param.dealerPrefecture,
              DealerCityCountry: param.dealerCityCountry,
              DealerTownStreetAddress: param.dealerTownStreetAddress,
              DealerBuildingNameAndRoomNumber: param.dealerBuildingNameAndRoomNumber,
              DealerMobilePhoneNumber: param.dealerMobilePhoneNumber,
              DealerMailAddress: param.dealerMailAddress,
              DealerDepartment: param.dealerDepartment,
              DealerOtherDepartment: param.dealerOtherDepartment,
              DealerPosition: param.dealerPosition,
              AboutRuler1: param.aboutRuler1 === AboutRulerType.None ? null : param.aboutRuler1,
              Ruler1Relationship: param.ruler1Relationship === RelastionShipType.None ? null : param.ruler1Relationship,
              Ruler1Name: param.ruler1Name,
              Ruler1BirthDay: formatDate(param.ruler1BirthDay, DateFormatType.Slash_yyyyMMdd),
              Ruler1MailAddress: param.ruler1MailAddress,
              Ruler1PostCode: param.ruler1PostCode,
              Ruler1Prefecture: param.ruler1Prefecture,
              Ruler1CityCountry: param.ruler1CityCountry,
              Ruler1TownStreetAddress: param.ruler1TownStreetAddress,
              Ruler1BuildingNameAndRoomNumber: param.ruler1BuildingNameAndRoomNumber,
              Ruler1ImportantPublicWorker:
                param.ruler1ImportantPublicWorker === RulerImportantPublicWorkerType.None
                  ? null
                  : param.ruler1ImportantPublicWorker === RulerImportantPublicWorkerType.Yes,
              AboutRuler2: param.aboutRuler2 === AboutRulerType.None ? null : param.aboutRuler2,
              Ruler2Relationship: param.ruler2Relationship === RelastionShipType.None ? null : param.ruler2Relationship,
              Ruler2Name: param.ruler2Name,
              Ruler2BirthDay: formatDate(param.ruler2BirthDay, DateFormatType.Slash_yyyyMMdd),
              Ruler2MailAddress: param.ruler2MailAddress,
              Ruler2PostCode: param.ruler2PostCode,
              Ruler2Prefecture: param.ruler2Prefecture,
              Ruler2CityCountry: param.ruler2CityCountry,
              Ruler2TownStreetAddress: param.ruler2TownStreetAddress,
              Ruler2BuildingNameAndRoomNumber: param.ruler2BuildingNameAndRoomNumber,
              Ruler2ImportantPublicWorker:
                param.ruler2ImportantPublicWorker === RulerImportantPublicWorkerType.None
                  ? null
                  : param.ruler2ImportantPublicWorker === RulerImportantPublicWorkerType.Yes,
              AboutRuler3: param.aboutRuler3 === AboutRulerType.None ? null : param.aboutRuler3,
              Ruler3Relationship: param.ruler3Relationship === RelastionShipType.None ? null : param.ruler3Relationship,
              Ruler3Name: param.ruler3Name,
              Ruler3BirthDay: formatDate(param.ruler3BirthDay, DateFormatType.Slash_yyyyMMdd),
              Ruler3MailAddress: param.ruler3MailAddress,
              Ruler3PostCode: param.ruler3PostCode,
              Ruler3Prefecture: param.ruler3Prefecture,
              Ruler3CityCountry: param.ruler3CityCountry,
              Ruler3TownStreetAddress: param.ruler3TownStreetAddress,
              Ruler3BuildingNameAndRoomNumber: param.ruler3BuildingNameAndRoomNumber,
              Ruler3ImportantPublicWorker:
                param.ruler3ImportantPublicWorker === RulerImportantPublicWorkerType.None
                  ? null
                  : param.ruler3ImportantPublicWorker === RulerImportantPublicWorkerType.Yes,
              InvestorCategoryInfo: param.investorCategoryInfo
                .filter((x) => x.checked)
                .map((x) => findInvestorCategoryInfoId(x.label) ?? null),
              ConsentDate: param.consentDate ?? null,
              DownloadGeneralInvestorPdf: param.downloadConsentGeneralInvestorPdf ?? false,
              TwoFactorAuthenticationType: param.authMode ?? AuthModeSelectType.None,
            },
          },
        };
        const openAccountData = await openAccount(data);
        if (!openAccountData?.data) return;

        if (openAccountData.data.OpenAccount?.Code === 0) {
          setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.entryComplete);
        } else {
          showError(openAccountData.data.OpenAccount?.Message);
        }
      },
    },
    [showError],
  );

  const executeUpdateAccount = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const data: ChangeAccountInfoMutationVariables = {
          input: {
            HaveEKYCDocument: param.identificationQuestion,
            CanTakePicture: param.selfieQuestion,
            ConfirmResponsibility: param.confirmResponsibility,
            CorporateNumber: param.corporateNumber,
            Name: param.corporateName,
            Furigana: param.corporateFurigana,
            PostCode: param.corporatePostCode,
            Prefecture: param.corporatePrefecture,
            CityCountry: param.corporateCityCountry,
            TownStreetAddress: param.corporateTownStreetAddress,
            BuildingNameAndRoomNumber: param.corporateBuildingNameAndRoomNumber,
            RegisteredPostCode: param.registeredCorporatePostCode,
            RegisteredPrefecture: param.registeredCorporatePrefecture,
            RegisteredCityCountry: param.registeredCorporateCityCountry,
            RegisteredTownStreetAddress: param.registeredCorporateTownStreetAddress,
            RegisteredBuildingNameAndRoomNumber: param.registeredCorporateBuildingNameAndRoomNumber,
            CorporateSettlementMonth: [findSettlementMonthId(param.corporateSettlementMonth)!],
            CorporateSettlementDay: findSettlementDayId(param.corporateSettlementDay),
            CorporateEstablishmentDate: emptyToEpochDate(formatDate(param.corporateEstablishmentDate, DateFormatType.Slash_yyyyMMdd)),
            CorporateMainBusinessContent: findCorporateBusinessContentId(param.corporateMainBusinessContent),
            CorporateMainBusinessContentOtherText: param.corporateMainBusinessContentOtherText,
            CorporateCapital: Number(param.corporateCapital),
            CorporateNumberOfEmployees: Number(param.corporateNumberOfEmployees),
            CorporateHomepage: param.corporateHomepage,
            LeaseFundPurpose: param.leaseFundPurpose
              .filter((x) => x.checked)
              .map((x) => findCorporateLeaseFundPurposeId(x.label) ?? null),
            LeaseFundPurposeOtherText: param.leaseFundPurposeOtherText,
            DomesticRealEstatePurpose: param.domesticRealEstatePurpose
              .filter((x) => x.checked)
              .map((x) => findDomesticRealEstatePurposeId(x.label) ?? null),
            DomesticRealEstatePurposeOtherText: param.domesticRealEstatePurposeOtherText,
            OverseasRealEstatePurpose: param.overseasRealEstatePurpose
              .filter((x) => x.checked)
              .map((x) => findCorporateOverseasRealEstatePurposeId(x.label) ?? null),
            OverseasRealEstatePurposeOtherText: param.overseasRealEstatePurposeOtherText,
            ContactPhoneNumber: param.contactPhoneNumber,
            ContactFaxNumber: param.contactFaxNumber,
            RepresentFirstName: param.representFirstName,
            RepresentLastName: param.representLastName,
            RepresentFirstNameKana: param.representFirstNameKana,
            RepresentLastNameKana: param.representLastNameKana,
            RepresentBirthDay: emptyToEpochDate(formatDate(param.representBirthDay, DateFormatType.Slash_yyyyMMdd)) ,
            RepresentPostCode: param.representPostCode,
            RepresentPrefecture: param.representPrefecture,
            RepresentCityCountry: param.representCityCountry,
            RepresentTownStreetAddress: param.representTownStreetAddress,
            RepresentBuildingNameAndRoomNumber: param.representBuildingNameAndRoomNumber,
            RepresentMobilePhoneNumber: param.representMobilePhoneNumber,
            RepresentMailAddress: param.representMailAddress,
            RepresentPosition: param.representPosition,
            DealerFirstName: param.dealerFirstName,
            DealerLastName: param.dealerLastName,
            DealerFirstNameKana: param.dealerFirstNameKana,
            DealerLastNameKana: param.dealerLastNameKana,
            DealerBirthDay: emptyToEpochDate(formatDate(param.dealerBirthDay, DateFormatType.Slash_yyyyMMdd)),
            DealerPostCode: param.dealerPostCode,
            DealerPrefecture: param.dealerPrefecture,
            DealerCityCountry: param.dealerCityCountry,
            DealerTownStreetAddress: param.dealerTownStreetAddress,
            DealerBuildingNameAndRoomNumber: param.dealerBuildingNameAndRoomNumber,
            DealerMobilePhoneNumber: param.dealerMobilePhoneNumber,
            DealerMailAddress: param.dealerMailAddress,
            DealerDepartment: param.dealerDepartment,
            DealerOtherDepartment: param.dealerOtherDepartment,
            DealerPosition: param.dealerPosition,
            AboutRuler1: param.aboutRuler1 === AboutRulerType.None ? null : param.aboutRuler1,
            Ruler1Relationship: param.ruler1Relationship === RelastionShipType.None ? null : param.ruler1Relationship,
            Ruler1Name: param.ruler1Name,
            Ruler1BirthDay: emptyToEpochDate(formatDate(param.ruler1BirthDay, DateFormatType.Slash_yyyyMMdd)),
            Ruler1MailAddress: param.ruler1MailAddress,
            Ruler1PostCode: param.ruler1PostCode,
            Ruler1Prefecture: param.ruler1Prefecture,
            Ruler1CityCountry: param.ruler1CityCountry,
            Ruler1TownStreetAddress: param.ruler1TownStreetAddress,
            Ruler1BuildingNameAndRoomNumber: param.ruler1BuildingNameAndRoomNumber,
            Ruler1ImportantPublicWorker:
              param.ruler1ImportantPublicWorker === RulerImportantPublicWorkerType.None
                ? null
                : param.ruler1ImportantPublicWorker === RulerImportantPublicWorkerType.Yes,
            AboutRuler2: param.aboutRuler2 === AboutRulerType.None ? null : param.aboutRuler2,
            Ruler2Relationship: param.ruler2Relationship === RelastionShipType.None ? null : param.ruler2Relationship,
            Ruler2Name: param.ruler2Name,
            Ruler2BirthDay: emptyToEpochDate(formatDate(param.ruler2BirthDay, DateFormatType.Slash_yyyyMMdd)),
            Ruler2MailAddress: param.ruler2MailAddress,
            Ruler2PostCode: param.ruler2PostCode,
            Ruler2Prefecture: param.ruler2Prefecture,
            Ruler2CityCountry: param.ruler2CityCountry,
            Ruler2TownStreetAddress: param.ruler2TownStreetAddress,
            Ruler2BuildingNameAndRoomNumber: param.ruler2BuildingNameAndRoomNumber,
            Ruler2ImportantPublicWorker:
              param.ruler2ImportantPublicWorker === RulerImportantPublicWorkerType.None
                ? null
                : param.ruler2ImportantPublicWorker === RulerImportantPublicWorkerType.Yes,
            AboutRuler3: param.aboutRuler3 === AboutRulerType.None ? null : param.aboutRuler3,
            Ruler3Relationship: param.ruler3Relationship === RelastionShipType.None ? null : param.ruler3Relationship,
            Ruler3Name: param.ruler3Name,
            Ruler3BirthDay: emptyToEpochDate(formatDate(param.ruler3BirthDay, DateFormatType.Slash_yyyyMMdd)),
            Ruler3MailAddress: param.ruler3MailAddress,
            Ruler3PostCode: param.ruler3PostCode,
            Ruler3Prefecture: param.ruler3Prefecture,
            Ruler3CityCountry: param.ruler3CityCountry,
            Ruler3TownStreetAddress: param.ruler3TownStreetAddress,
            Ruler3BuildingNameAndRoomNumber: param.ruler3BuildingNameAndRoomNumber,
            Ruler3ImportantPublicWorker:
              param.ruler3ImportantPublicWorker === RulerImportantPublicWorkerType.None
                ? null
                : param.ruler3ImportantPublicWorker === RulerImportantPublicWorkerType.Yes,
            InvestorCategoryInfo: param.investorCategoryInfo
              .filter((x) => x.checked)
              .map((x) => findInvestorCategoryInfoId(x.label) ?? null),
            TwoFactorAuthenticationType: param.authMode ?? AuthModeSelectType.None,
            Fatca: param.fatca,
            TransferBankName: param.bankName,
            TransferBranchName: param.branchName,
            TransferAccountType: param.accountType,
            TransferAccountNumber: param.accountNumber,
            TransferAccountHolderName: param.accountHolderName,
            InvoiceNumber: param.invoiceNumber,
            HasNoInvoiceNumber: param.hasNoInvoiceNumber,
          },
        };

        const changeAccountData = await changeAccount(data);
        if (!changeAccountData?.data) return;

        if (changeAccountData.data.ChangeAccountInfo?.Code === 0) {
          setPageStep && setPageStep(PageStep.COMPLETE);
        } else {
          showError(changeAccountData.data.ChangeAccountInfo?.Message);
        }
      },
    },
    [showError],
  );

  const goBack = {
    onClick: () => {
      if (isUpdate) {
        setPageStep && setPageStep(PageStep.UPDATE);
      } else {
        setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.tfAuthentication);
      }
    },
  };

  const displayInfo = {
    corporateInfo: [
      { label: '法人番号', value: param.corporateNumber },
      { label: '法人名', value: param.corporateName },
      { label: '法人名（フリガナ）', value: param.corporateFurigana },
      {
        label: '法人住所',
        value: `${param.corporatePostCode} ${param.corporatePrefecture}${param.corporateCityCountry}${param.corporateTownStreetAddress}${param.corporateBuildingNameAndRoomNumber}`,
      },
      { label: '法人住所の確認', value: getCorporateAddressConfirm(param.corporateAddressConfirm) },
      {
        label: '登記上の法人住所',
        value: `${param.registeredCorporatePostCode} ${param.registeredCorporatePrefecture}${param.registeredCorporateCityCountry}${param.registeredCorporateTownStreetAddress}${param.registeredCorporateBuildingNameAndRoomNumber}`,
        hidden: param.corporateAddressConfirm !== CorporateAddressConfirmSelectType.Different,
      },
      {
        label: '決算日',
        value: `${param.corporateSettlementMonth}${param.corporateSettlementDay}`,
      },
      { label: '設立年月日', value: formatDate(param.corporateEstablishmentDate, DateFormatType.Japanese_yyyyMd) },
      {
        label: '事業内容',
        value: `${
          param.corporateMainBusinessContent +
          `${param.corporateMainBusinessContentOtherText ? `(${param.corporateMainBusinessContentOtherText})` : ''}`
        }`,
      },
      { label: '資本金', value: `${Number(param.corporateCapital).toLocaleString()} 円` },
      { label: '従業員数', value: `${Number(param.corporateNumberOfEmployees).toLocaleString()} 人` },
      { label: '事業内容が確認できるホームページ', value: param.corporateHomepage },
    ] as DisplayItemProps[],
    transactionPurpose: [
      {
        label: '日本型オペレーティング・リース案件',
        value:
          param.leaseFundPurpose
            .filter((ck) => ck.checked)
            .map((ck) => ck.label)
            .join('／') + (param.leaseFundPurposeOtherText ? `（${param.leaseFundPurposeOtherText}）` : ''),
      },
      {
        label: '不動産小口化商品',
        value:
          param.domesticRealEstatePurpose
            .filter((ck) => ck.checked)
            .map((ck) => ck.label)
            .join('／') +
          (param.domesticRealEstatePurposeOtherText ? `（${param.domesticRealEstatePurposeOtherText}）` : ''),
      },
      {
        label: '海外不動産案件',
        value:
          param.overseasRealEstatePurpose
            .filter((ck) => ck.checked)
            .map((ck) => ck.label)
            .join('／') +
          (param.overseasRealEstatePurposeOtherText ? `（${param.overseasRealEstatePurposeOtherText}）` : ''),
      },
    ] as DisplayItemProps[],
    contactInfo: [
      {
        label: '電話番号',
        value: param.contactPhoneNumber,
      },
      { label: 'FAX番号', value: param.contactFaxNumber },
    ] as DisplayItemProps[],
    representInfo: [
      {
        label: '代表者氏名',
        value: param.representLastName
          ? `${param.representLastName} ${param.representFirstName}(${param.representLastNameKana} ${param.representFirstNameKana})`
          : '',
      },
      { label: '生年月日', value: formatDate(param.representBirthDay, DateFormatType.Japanese_yyyyMd) },
      {
        label: '代表者住所',
        value: param.representPostCode
          ? `${param.representPostCode} ${param.representPrefecture}${param.representCityCountry}${param.representTownStreetAddress}${param.representBuildingNameAndRoomNumber}`
          : '',
      },
      {
        label: '代表者携帯番号',
        value: param.representMobilePhoneNumber,
      },
      {
        label: 'メールアドレス',
        value: param.representMailAddress,
      },
      {
        label: 'ご役職',
        value: param.representPosition,
      },
    ] as DisplayItemProps[],
    dealerInfo: [
      {
        label: '取引担当者氏名',
        value: `${param.dealerLastName} ${param.dealerFirstName}(${param.dealerLastNameKana} ${param.dealerFirstNameKana})`,
      },
      { label: '生年月日', value: formatDate(param.dealerBirthDay, DateFormatType.Japanese_yyyyMd) },
      {
        label: '取引担当者住所',
        value: `${param.dealerPostCode} ${param.dealerPrefecture}${param.dealerCityCountry}${param.dealerTownStreetAddress}${param.dealerBuildingNameAndRoomNumber}`,
      },
      {
        label: '取引担当者携帯電話番号',
        value: param.dealerMobilePhoneNumber,
      },
      {
        label: 'メールアドレス',
        value: param.dealerMailAddress,
      },
      {
        label: '所属部署',
        value: `${param.dealerDepartment}${param.dealerOtherDepartment ? `／${param.dealerOtherDepartment}` : ''}`,
      },
      {
        label: 'ご役職',
        value: param.dealerPosition,
      },
    ] as DisplayItemProps[],
    ruler1Info: [
      {
        label: '実質的支配者について',
        value: getAboutRuler(param.aboutRuler1),
      },
      {
        label: '実質的支配者と貴社とのご関係',
        value: findRelastionShipName(param.ruler1Relationship),
      },
      {
        label: '氏名（上場法人等の場合は名称）',
        value: param.ruler1Name,
      },
      {
        label: '生年月日　※上場法人等の場合は1900/01/01とご記入ください',
        value: formatDate(param.ruler1BirthDay, DateFormatType.Japanese_yyyyMd),
      },
      {
        label: 'メールアドレス',
        value: param.ruler1MailAddress,
      },
      {
        label: '住所（上場法人の場合は本店所在地）',
        value: `${param.ruler1PostCode} ${param.ruler1Prefecture}${param.ruler1CityCountry}${param.ruler1TownStreetAddress}${param.ruler1BuildingNameAndRoomNumber}`,
      },
      {
        label: '外国の重要な公人への該当性',
        value: getImportantPublicWorkerText(param.ruler1ImportantPublicWorker),
      },
    ] as DisplayItemProps[],
    ruler2Info:
      param.addtionalRuler2 &&
      ([
        {
          label: '実質的支配者について',
          value: getAboutRuler(param.aboutRuler2),
        },
        {
          label: '実質的支配者と貴社とのご関係',
          value: findRelastionShipName(param.ruler2Relationship),
        },
        {
          label: '氏名（上場法人等の場合は名称）',
          value: param.ruler2Name,
        },
        {
          label: '生年月日　※上場法人等の場合は1900/01/01とご記入ください',
          value: formatDate(param.ruler2BirthDay, DateFormatType.Japanese_yyyyMd),
        },
        {
          label: 'メールアドレス',
          value: param.ruler2MailAddress,
        },
        {
          label: '住所（上場法人の場合は本店所在地）',
          value: `${param.ruler2PostCode} ${param.ruler2Prefecture}${param.ruler2CityCountry}${param.ruler2TownStreetAddress}${param.ruler2BuildingNameAndRoomNumber}`,
        },
        {
          label: '外国の重要な公人への該当性',
          value: getImportantPublicWorkerText(param.ruler2ImportantPublicWorker),
        },
      ] as DisplayItemProps[]),
    ruler3Info:
      param.addtionalRuler3 &&
      ([
        {
          label: '実質的支配者について',
          value: getAboutRuler(param.aboutRuler3),
        },
        {
          label: '実質的支配者と貴社とのご関係',
          value: findRelastionShipName(param.ruler3Relationship),
        },
        {
          label: '氏名（上場法人等の場合は名称）',
          value: param.ruler3Name,
        },
        {
          label: '生年月日　※上場法人等の場合は1900/01/01とご記入ください',
          value: formatDate(param.ruler3BirthDay, DateFormatType.Japanese_yyyyMd),
        },
        {
          label: 'メールアドレス',
          value: param.ruler3MailAddress,
        },
        {
          label: '住所（上場法人の場合は本店所在地）',
          value: `${param.ruler3PostCode} ${param.ruler3Prefecture}${param.ruler3CityCountry}${param.ruler3TownStreetAddress}${param.ruler3BuildingNameAndRoomNumber}`,
        },
        {
          label: '外国の重要な公人への該当性',
          value: getImportantPublicWorkerText(param.ruler3ImportantPublicWorker),
        },
      ] as DisplayItemProps[]),
    InvestorCategoryInfo: [
      {
        label: '投資家区分',
        value: param.investorCategoryInfo
          .filter((ck) => ck.checked)
          .map((ck) => ck.label)
          .join('／'),
      },
      {
        label: '特定投資家以外の顧客としての取り扱いの申出',
        value:
          param.investorCategory == InvestorCategory.GeneralInvestor
            ? 'あり'
            : param.investorCategory == InvestorCategory.SpecificInvestor
              ? 'なし'
              : '',
      },
    ] as DisplayItemProps[],
    tfAuthentication: [
      {
        label: '2段階認証の方法',
        value: getTwoAuthenticationText(param.authMode),
      },
    ] as DisplayItemProps[],
    fatca: [
      {
        label: '米国納税義務（FATCA）に関する確認',
        value: getFatcaText(param.fatca),
      },
    ] as DisplayItemProps[],
    bankInfo: [
      {
        label: '配当金振込用口座',
        value: `${
          hasNullBankInfo(param)
            ? `${param.bankName}／${param.branchName}／${getAccountType(param.accountType)}／${param.accountNumber}／${
                param.accountHolderName
              }`
            : ''
        }`,
      },
    ] as DisplayItemProps[],
    invoiceNumber: [
      {
        label: 'インボイス番号',
        value: `${param.hasNoInvoiceNumber ? 'なし' : param.invoiceNumber}`,
      },
    ] as DisplayItemProps[],
  };

  return { agreeToAccountOpenProfileDetailsProps, execute, goBack, displayInfo };
};

const hasNullBankInfo = (param: CorporateAccountEstablishRequestParam) => {
  return param.bankName && param.branchName && param.accountType && param.accountNumber && param.accountHolderName;
};

const getCorporateAddressConfirm = (type: CorporateAddressConfirmSelectType) => {
  switch (type) {
    case CorporateAddressConfirmSelectType.Same:
      return '登記上の住所（本店・主たる事務所/支店・従たる事務所）を入力しました';
    case CorporateAddressConfirmSelectType.Different:
      return '登記上の住所以外の住所を入力しました';
    default:
      return '';
  }
};

const getAboutRuler = (type: AboutRulerType) => {
  switch (type) {
    case AboutRulerType.Same:
      return '法人を代表し、その業務を執行する個人';
    case AboutRulerType.Different:
      return '代表者とは異なる個人、または上場法人等に該当';
    default:
      return '';
  }
};

const getImportantPublicWorkerText = (type: RulerImportantPublicWorkerType) => {
  switch (type) {
    case RulerImportantPublicWorkerType.No:
      return '上記の実質的支配者は、外国の重要な公人もしくはその親族には該当しません';
    case RulerImportantPublicWorkerType.Yes:
      return '上記の実質的支配者は、外国の重要な公人もしくはその親族に該当します';
    default:
      return '';
  }
};
const getTwoAuthenticationText = (type: AuthModeSelectType) => {
  switch (type) {
    case AuthModeSelectType.SMS:
      return 'SMSによる認証';
    case AuthModeSelectType.MailAddress:
      return 'メールによる認証';
    case AuthModeSelectType.None:
    default:
      return '';
  }
};

const getFatcaText = (type: FatcaStatus | null) => {
  switch (type) {
    case FatcaStatus.JapaneseCompany:
      return '日本企業';
    case FatcaStatus.UsCompany:
      return '米国法人、米国所有外国事業体';
    default:
      return '';
  }
};
const getAccountType = (type: AccountType | null) => {
  switch (type) {
    case AccountType.Ordinary:
      return '普通';
    case AccountType.Checking:
      return '当座';
    case AccountType.Savings:
      return '貯蓄';
    case AccountType.Other:
      return 'その他';
    case AccountType.None:
    default:
      return '';
  }
};

const emptyToEpochDate = (
  value: string | null | undefined
): string | null | undefined => {
  if (value === "") return "1700/01/01";
  return value;
};